import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { UserInfo, LoginRequest, RegisterRequest } from '@/types'
import { apiClient } from '@/services/apiClient'
import { unsubscribeFromPush } from '@/services/notificationService'

interface AuthContextType {
  user: UserInfo | null
  loading: boolean
  login: (credentials: LoginRequest) => Promise<void>
  register: (data: RegisterRequest) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserInfo | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshUser = async () => {
    try {
      const userData = await apiClient.get<UserInfo>('/api/auth/user')
      if (userData.isAuthenticated) {
        setUser(userData)
      } else {
        setUser(null)
      }
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refreshUser()
  }, [])

  useEffect(() => {
    const handleAuthExpired = () => {
      setUser(null)
    }
    window.addEventListener('auth-expired', handleAuthExpired)
    return () => window.removeEventListener('auth-expired', handleAuthExpired)
  }, [])

  const login = async (credentials: LoginRequest) => {
    const userData = await apiClient.post<UserInfo>('/api/auth/login', credentials)
    setUser(userData)
  }

  const register = async (data: RegisterRequest) => {
    const userData = await apiClient.post<UserInfo>('/api/auth/register', data)
    setUser(userData)
  }

  const logout = async () => {
    // Clean up push subscription and notifications before signing out
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready
        const subscription = await registration.pushManager.getSubscription()
        if (subscription) {
          await unsubscribeFromPush(subscription.endpoint)
          await subscription.unsubscribe()
        }
        // Dismiss all active notifications
        const notifications = await registration.getNotifications()
        notifications.forEach(n => n.close())
      }
      // Clear app badge
      await (navigator as Navigator & { clearAppBadge?: () => Promise<void> }).clearAppBadge?.()
    } catch {
      // Don't block logout if notification cleanup fails
    }

    await apiClient.post('/api/auth/logout')
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
